import { useState, useEffect, useMemo } from "react"
import { Dialog, DialogPanel } from "@headlessui/react"
import { Link } from "react-router-dom"
import { MenuIcon, XIcon } from "lucide-react"
import { Logo } from "../../../shared/ui/components/custom/logo"
import { cn } from "@/lib/utils"
import { useAuth } from "@/modules/auth/hooks/use-auth"
import { AppRole } from "@/modules/auth/constants/app-role"
import { Button } from "@/modules/shared/ui/components/button"

enum ScrollState {
  Top = "top",
  Transparent = "transparent",
  Hidden = "hidden",
}

const navigation = [
  { name: "Home", href: "/#home" },
  { name: "How it works", href: "/#how-it-works" },
  { name: "Features", href: "/#showcase" },
  { name: "AI Features", href: "/#ai-features" },
  { name: "Integrations", href: "/#integrations" },
  // { name: "Testimonials", href: "/#testimonials" },
  // { name: "Pricing", href: "/#pricing" },
]

export function Header({ className }: { className?: string }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [scrollState, setScrollState] = useState<ScrollState>(ScrollState.Top)
  const { isAuthenticated, user, logout, hasRole } = useAuth()

  const displayName =
    `${user?.user.firstName ?? ""} ${user?.user.lastName ?? ""}`.trim() || ""

  const dashboardPath = useMemo(
    () => (hasRole(AppRole.Employee) ? "/employee/dashboard" : "/dashboard"),
    [hasRole],
  )

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY

      if (scrollPosition < 50) {
        setScrollState(ScrollState.Top)
      } else if (scrollPosition < 200) {
        setScrollState(ScrollState.Transparent)
      } else {
        setScrollState(ScrollState.Hidden)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    if (href.startsWith("/#")) {
      e.preventDefault()
      const id = href.substring(2)
      const element = document.getElementById(id)
      if (element) {
        const offset = 80
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset
        const offsetPosition = elementPosition - offset

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        })
      }
    }
  }

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 w-full transition-all duration-500 ease-in-out lg:my-3 lg:mx-6 lg:inset-x-6 lg:w-auto lg:rounded-full",
        scrollState === ScrollState.Top &&
        "opacity-100 lg:bg-white/10 lg:backdrop-blur-md lg:border lg:border-white/20 lg:shadow-lg",
        scrollState === ScrollState.Transparent &&
        "bg-transparent backdrop-blur-none opacity-50",
        scrollState === ScrollState.Hidden && "opacity-0 pointer-events-none",
        className,
      )}
    >
      <nav
        className="flex items-center justify-between px-3 py-2 sm:px-4 lg:px-4 lg:py-1.5"
        aria-label="Global"
      >
        {/* Logo */}
        <Link to="/">
          <span className="sr-only">TEMPERED TALENT</span>
          <Logo size="md" className="h-5 w-auto sm:h-6 bg-white backdrop-blur-md rounded-full p-1 shadow-md" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-center lg:items-center lg:gap-x-8">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className="text-lg font-semibold leading-6 text-white hover:text-white/75 transition-colors"
            >
              {item.name}
            </Link>
          ))}
        </div>

        {/* Desktop auth area */}
        <div className="hidden gap-3 lg:flex lg:items-center">
          {isAuthenticated && (
            <span className="text-xs sm:text-sm text-white max-w-[180px] truncate">
              {displayName}
            </span>
          )}

          {!isAuthenticated && (
            <>
              <span className="text-white/40">|</span>
              <Link
                to={"/login"}
                className="cursor-pointer gap-2 text-lg font-medium text-white hover:text-white/75 transition-colors"
              >
                Log in
              </Link>
              <Link to="/signup">
                <Button
                  className="rounded-full flex items-center gap-5 text-[#1A237E] px-4 py-7 shadow-md ml-4 cursor-pointer bg-cyan-400 hover:bg-cyan-300 transition-colors duration-200 font-semibold text-lg"
                >
                  Get Started
                  <div className="bg-[#1A237E] rounded-full p-1.5">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      strokeWidth="2"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                        transform="rotate(-45 10 10)"
                      />
                    </svg>
                  </div>
                </Button>
              </Link>
            </>
          )}

          {isAuthenticated && (
            <>
              <span className="text-white/40">|</span>
              <Link to={dashboardPath}>
                <Button
                  className="rounded-full flex items-center gap-2 text-[#1A237E] px-4 py-2 shadow-md cursor-pointer bg-cyan-400 hover:bg-cyan-300 transition-colors duration-200 font-semibold text-sm"
                >
                  Dashboard
                </Button>
              </Link>
              <button
                className="cursor-pointer text-lg font-medium text-white hover:text-white/75 transition-colors"
                onClick={logout}
              >
                Logout
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger menu Button - positioned to the right */}
        <div className="flex lg:hidden">
          <Button
            variant="ghost"
            className="-m-2.5 inline-flex items-center text-extrabold justify-center rounded-md p-2.5 text-white hover:bg-cyan-400 transition-colors"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open main menu"
          >
            <MenuIcon className="h-8 w-8" aria-hidden="true" />
          </Button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-background px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              className="-m-1.5 p-1.5"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Tempered Talent</span>
              <Logo className="h-8 w-auto" />
            </Link>
            <Button
              variant="ghost"
              className="-m-2.5 rounded-md p-2.5 text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
            >
              <XIcon className="h-6 w-6" aria-hidden="true" />
            </Button>
          </div>

          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              {/* Navigation links */}
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-muted transition-colors"
                    onClick={(e) => {
                      handleNavClick(e, item.href)
                      setMobileMenuOpen(false)
                    }}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>

              {/* Auth section (mobile) */}
              <div className="py-6 space-y-3">
                {isAuthenticated ? (
                  <>
                    {displayName && (
                      <div className="px-3 py-2 bg-muted rounded-lg">
                        <p className="text-xs text-muted-foreground mb-1">
                          Signed in as
                        </p>
                        <p className="text-sm font-semibold text-foreground truncate">
                          {displayName}
                        </p>
                      </div>
                    )}
                    <Link
                      to="/dashboard"
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-muted transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setMobileMenuOpen(false)
                        logout()
                      }}
                      className="-mx-3 block w-full text-left rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-red-600 hover:bg-red-50 transition-colors"
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="-mx-3 block w-full rounded-lg px-3 py-2.5 text-center text-base font-semibold leading-7 text-foreground hover:bg-muted border border-border transition-colors"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="-mx-3 block w-full rounded-lg px-3 py-2.5 text-center text-base font-semibold leading-7 text-white bg-[#1A237E] hover:bg-[#0D1B69] transition-colors"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  )
}
